// Template CRUD service.
//
// Hits the `email_templates` table (shared for email + SMS via the `channel`
// column added by migration 007). All calls require an authenticated user;
// RLS retains owner access; managers/admins also share SMS templates.
//
// Prereq: migration 007_templates_foundation.sql MUST be run in Supabase
// before this service will work end-to-end. Service calls assume the
// post-migration schema (channel / updated_at / variables columns present).
// Until the migration runs, queries here will error out -- the Templates UI
// surfaces that to the user.

import { supabase } from "./supabase";
import type { EmailTemplate, EmailTemplateType } from "../types";
import {
  extractTemplateVariables,
  isKnownTemplateVariableKey,
} from "./templateInterpolation";

export type TemplateChannel = "email" | "sms" | "instagram_dm";

export interface CreateTemplateInput {
  name: string;
  subject: string;
  body: string;
  template_type: EmailTemplateType;
  channel?: TemplateChannel;
  is_default?: boolean;
}

export type UpdateTemplateInput = Partial<CreateTemplateInput>;

/**
 * Scan subject + body for declared placeholders, dedupe, and filter to the
 * v1 TemplateContext namespace before writing to the database.
 *
 * This is the canonical save-path filter referenced by the contract note
 * on extractTemplateVariables (frontend/src/lib/templateInterpolation.ts).
 * Exposed so it can be unit-tested without hitting Supabase.
 */
export function computeDeclaredVariables(
  subject: string,
  body: string
): string[] {
  const all = [
    ...extractTemplateVariables(subject),
    ...extractTemplateVariables(body),
  ];
  const unique = Array.from(new Set(all));
  return unique.filter(isKnownTemplateVariableKey);
}

async function requireUserId(): Promise<string> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user) throw new Error("User not authenticated");
  return user.id;
}

async function clearDefaultInPartition(
  userId: string,
  channel: TemplateChannel,
  templateType: EmailTemplateType,
  exceptId?: string
): Promise<void> {
  // Demotes any other templates marked is_default in the same
  // (user_id, channel, template_type) partition so the partial unique
  // index added by migration 007 does not reject the promotion.
  let q = supabase
    .from("email_templates")
    .update({ is_default: false })
    .eq("user_id", userId)
    .eq("channel", channel)
    .eq("template_type", templateType)
    .eq("is_default", true);
  if (exceptId) q = q.neq("id", exceptId);
  const { error } = await q;
  if (error) throw error;
}

export const templateService = {
  async listTemplates(channel?: TemplateChannel): Promise<EmailTemplate[]> {
    let query = supabase
      .from("email_templates")
      .select("*")
      .order("name", { ascending: true });
    if (channel) query = query.eq("channel", channel);
    const { data, error } = await query;
    if (error) throw error;
    return (data as EmailTemplate[]) ?? [];
  },

  async getTemplate(id: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from("email_templates")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return (data as EmailTemplate | null) ?? null;
  },

  async getDefaultTemplate(
    channel: TemplateChannel,
    templateType: EmailTemplateType
  ): Promise<EmailTemplate | null> {
    // Returns null when no default exists in the partition; SendListingsDialog
    // uses that as the signal to fall back to hardcoded literals.
    // Shared SMS visibility must not make per-owner defaults ambiguous.
    const userId = await requireUserId();
    const { data, error } = await supabase
      .from("email_templates")
      .select("*")
      .eq("channel", channel)
      .eq("template_type", templateType)
      .eq("is_default", true)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw error;
    return (data as EmailTemplate | null) ?? null;
  },

  async createTemplate(input: CreateTemplateInput): Promise<EmailTemplate> {
    const userId = await requireUserId();
    const channel: TemplateChannel = input.channel ?? "email";
    const variables = computeDeclaredVariables(input.subject, input.body);
    const row = {
      user_id: userId,
      name: input.name,
      subject: input.subject,
      body: input.body,
      template_type: input.template_type,
      channel,
      is_default: input.is_default ?? false,
      variables,
    };
    if (row.is_default) {
      await clearDefaultInPartition(userId, channel, input.template_type);
    }
    const { data, error } = await supabase
      .from("email_templates")
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return data as EmailTemplate;
  },

  async updateTemplate(
    id: string,
    updates: UpdateTemplateInput
  ): Promise<EmailTemplate> {
    const current = await templateService.getTemplate(id);
    if (!current) throw new Error("Template not found");

    const patch: Record<string, unknown> = { ...updates };

    // Recompute declared variables on any subject or body change so the
    // `variables` column stays in sync with the actual template body.
    if (updates.subject !== undefined || updates.body !== undefined) {
      patch.variables = computeDeclaredVariables(
        updates.subject ?? current.subject,
        updates.body ?? current.body
      );
    }

    if (updates.is_default === true) {
      const channel: TemplateChannel =
        (updates.channel ?? current.channel ?? "email") as TemplateChannel;
      const templateType = (updates.template_type ??
        current.template_type) as EmailTemplateType;
      await clearDefaultInPartition(current.user_id, channel, templateType, id);
    }

    const { data, error } = await supabase
      .from("email_templates")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data as EmailTemplate;
  },

  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from("email_templates")
      .delete()
      .eq("id", id);
    if (error) throw error;
  },

  async setDefault(id: string): Promise<EmailTemplate> {
    return templateService.updateTemplate(id, { is_default: true });
  },
};

export default templateService;
